import { PropsWithChildren, Suspense, lazy, useEffect } from 'react';
import { Route, Router, Switch, useLocation } from 'wouter';

import KaraokeAdLayout from '~/modules/Ads/KaraokeAdLayout';
import PageLoader from '~/modules/Elements/PageLoader';
import { liveTvRoutePaths } from '~/modules/live-tv/liveTvRoutePaths';
import LiveTvChannelPage from '~/modules/live-tv/pages/LiveTvChannelPage';
import LiveTvHomePage from '~/modules/live-tv/pages/LiveTvHomePage';
import Convert from '~/routes/Convert/Convert';
import Edit from '~/routes/Edit/Edit';
import GetSongsBPMs from '~/routes/Edit/GetSongsBPMs';
import RedirectToFirstLocalSong from '~/routes/Edit/RedirectToFirstLocalSong';
import ExcludeLanguages from '~/routes/ExcludeLanguages/ExcludeLanguages';
import Game from '~/routes/Game/Game';
import Jukebox from '~/routes/Jukebox/Jukebox';
import LandingPage from '~/routes/LandingPage/LandingPage';
import ManageSongs from '~/routes/ManageSongs/ManageSongs';
import QuickSetup from '~/routes/QuickSetup/QuickSetup';
import RemoteMic from '~/routes/RemoteMic/RemoteMic';
import SelectInput from '~/routes/SelectInput/SelectInput';
import { CalibrationSettings } from '~/routes/Settings/Calibration';
import RemoteMicSettings from '~/routes/Settings/RemoteMicSettings';
import Settings from '~/routes/Settings/Settings';
import SocialMediaElements from '~/routes/SocialMediaElements/SocialMediaElements';
import routePaths from '~/routes/routePaths';

import { AuthGuard } from '~/modules/Auth';
import KaraokeProviders from '../karaoke/KaraokeProviders';
import { normalizeAppPath, updateHeadLink } from '../shared/headLinks';

const LazySongList = lazy(() =>
  import('~/routes/ManageSongs/SongManagement').then((modules) => ({ default: modules.SongList })),
);

const LazySetlist = lazy(() => import('~/routes/Edit/Setlists').then((modules) => ({ default: modules.default })));

function RouteBrandingManager() {
  const [location] = useLocation();

  useEffect(() => {
    const pathname = normalizeAppPath(location);
    const liveTvBase = normalizeAppPath(liveTvRoutePaths.INDEX);
    const isLiveTv = pathname === liveTvBase || pathname.startsWith(liveTvBase + '/');
    const faviconHref = isLiveTv ? '/live-tv/logo.png?v=3' : '/logo.svg?v=3';

    updateHeadLink('icon', faviconHref);
    updateHeadLink('shortcut icon', faviconHref);
    updateHeadLink('apple-touch-icon', faviconHref);
  }, [location]);

  return null;
}

function KaraokeRoute({ children }: PropsWithChildren) {
  return <KaraokeAdLayout>{children}</KaraokeAdLayout>;
}

function RedirectToCatalog() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation('', { replace: true });
  }, [setLocation]);

  return null;
}

export default function CombinedApp() {
  return (
    <KaraokeProviders>
      <AuthGuard>
        <Router base={import.meta.env.BASE_URL}>
          <RouteBrandingManager />

          <Switch>
            <Route path={routePaths.MENU} component={RedirectToCatalog} />

            <Route
              path={routePaths.QUICK_SETUP}
              component={() => (
                <KaraokeRoute>
                  <QuickSetup />
                </KaraokeRoute>
              )}
            />
            <Route
              path={routePaths.EXCLUDE_LANGUAGES}
              component={() => (
                <KaraokeRoute>
                  <ExcludeLanguages />
                </KaraokeRoute>
              )}
            />
            <Route
              path={routePaths.JUKEBOX}
              component={() => (
                <KaraokeRoute>
                  <Jukebox />
                </KaraokeRoute>
              )}
            />

            <Route path={routePaths.GAME}>
              <KaraokeRoute>
                <Game />
              </KaraokeRoute>
            </Route>

            <Route
              path={routePaths.SELECT_INPUT}
              component={() => (
                <KaraokeRoute>
                  <SelectInput />
                </KaraokeRoute>
              )}
            />
            <Route
              path={routePaths.SETTINGS}
              component={() => (
                <KaraokeRoute>
                  <Settings />
                </KaraokeRoute>
              )}
            />
            <Route
              path={routePaths.SETTINGS_REMOTE_MICS}
              component={() => (
                <KaraokeRoute>
                  <RemoteMicSettings />
                </KaraokeRoute>
              )}
            />
            <Route
              path={routePaths.SETTINGS_CALIBRATION}
              component={() => (
                <KaraokeRoute>
                  <CalibrationSettings />
                </KaraokeRoute>
              )}
            />
            <Route path={routePaths.REMOTE_MIC} component={RemoteMic} />
            <Route path={routePaths.MANAGE_SONGS} component={ManageSongs} />
            <Route path="social-media-elements" component={SocialMediaElements} />
            <Route path={routePaths.CONVERT} component={() => <Convert />} />

            <Route
              path={routePaths.EDIT_SONGS_LIST}
              component={() => (
                <Suspense fallback={<PageLoader />}>
                  <LazySongList />
                </Suspense>
              )}
            />

            <Route path="edit/get-songs-bpms" component={GetSongsBPMs} />
            <Route path="edit/redirect-to-first-local-song" component={RedirectToFirstLocalSong} />

            <Route path={routePaths.EDIT_SONG}>
              <Edit />
            </Route>

            <Route
              path={routePaths.EDIT_SETLISTS}
              component={() => (
                <Suspense fallback={<PageLoader />}>
                  <LazySetlist />
                </Suspense>
              )}
            />

            <Route path={liveTvRoutePaths.CHANNEL} component={LiveTvChannelPage} />
            <Route path={liveTvRoutePaths.INDEX} component={LiveTvHomePage} />

            <Route path={routePaths.LANDING} component={LandingPage} />
            <Route
              path={routePaths.INDEX}
              component={() => (
                <KaraokeRoute>
                  <Game />
                </KaraokeRoute>
              )}
            />
          </Switch>
        </Router>
      </AuthGuard>
    </KaraokeProviders>
  );
}
